import { z } from "zod";
import { SystemEvent, SystemEntity } from "@erp/shared-types";
import { entityOfType } from "../utils/system-entities";

// ============================================================================
// Event Schemas
// ============================================================================

/**
 * Schema for EMAIL_INGESTED event
 * Emitted after an email is ingested and OCR has been performed
 */
export const EmailIngestedEventSchema = z.object({
  inboundEmailId: z.uuid(),
  organizationId: z.uuid(),
  to: z.array(z.string()).optional(),
  cc: z.array(z.string()).optional(),
  from: z.string().optional(),
  subject: z.string().optional(),
  messageId: z.string(),
  conversationId: z.string().optional(),
  documentIds: z.array(z.uuid()),
  mimeTypes: z.array(z.string()),
  attachmentCount: z.number(),
  classificationHints: z
    .array(z.string())
    .optional()
    .describe(
      "Hints about what workflows might match (e.g., 'ap_invoice', 'purchase_order')",
    ),
  routerHints: z
    .object({
      ap: z.boolean().optional(),
      po: z.boolean().optional(),
      quoteSheet: z.boolean().optional(),
    })
    .optional(),
});

export type EmailIngestedEvent = z.infer<typeof EmailIngestedEventSchema>;

/**
 * Schema for EMAIL_ROUTED_EMAIL_RULE event
 * Emitted after router determines email should be processed as email rule
 */
export const EmailRoutedEmailRuleEventSchema = z.object({
  emailRuleOptionId: z.uuid(),
  inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL),
});

export type EmailRoutedEmailRuleEvent = z.infer<
  typeof EmailRoutedEmailRuleEventSchema
>;
/**
 * Schema for EMAIL_ROUTED_AP_INVOICE event
 * Emitted after router determines email should be processed as AP invoice
 */
export const EmailRoutedAPInvoiceEventSchema = z.object({
  inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL),
});

export type EmailRoutedAPInvoiceEvent = z.infer<
  typeof EmailRoutedAPInvoiceEventSchema
>;

/**
 * Schema for EMAIL_ROUTED_PURCHASE_ORDER event
 * Emitted after router determines email should be processed as purchase order
 */
export const EmailRoutedPurchaseOrderEventSchema = z.object({
  inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL),
  organizationId: z.uuid(),
  documentIds: z.array(z.uuid()),
  routeReason: z.string().describe("Explanation of why this route was chosen"),
});

export type EmailRoutedPurchaseOrderEvent = z.infer<
  typeof EmailRoutedPurchaseOrderEventSchema
>;

/**
 * Schema for EMAIL_ROUTED_QUOTE_SHEET event
 * Emitted after router determines email should be processed as quote sheet
 */
export const EmailRoutedQuoteSheetEventSchema = z.object({
  inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL),
  organizationId: z.uuid(),
  documentIds: z.array(z.uuid()),
  routeReason: z.string().describe("Explanation of why this route was chosen"),
});

export type EmailRoutedQuoteSheetEvent = z.infer<
  typeof EmailRoutedQuoteSheetEventSchema
>;

/**
 * Schema for AP_INVOICE_DRAFT_CREATED event
 * Emitted after AP invoice draft is successfully created
 */
export const APInvoiceDraftCreatedEventSchema = z.object({
  apInvoice: entityOfType(SystemEntity.AP_INVOICE),
  inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL),
  organizationId: z.uuid(),
});

export const APInvoicePendingApprovalEventSchema = z.object({
  apInvoice: entityOfType(SystemEntity.AP_INVOICE),
});

export const APInvoiceNeedsReviewEventSchema = z.object({
  apInvoice: entityOfType(SystemEntity.AP_INVOICE),
});

export type APInvoiceDraftCreatedEvent = z.infer<
  typeof APInvoiceDraftCreatedEventSchema
>;

export type APInvoicePendingApprovalEvent = z.infer<
  typeof APInvoicePendingApprovalEventSchema
>;

export type APInvoiceNeedsReviewEvent = z.infer<
  typeof APInvoiceNeedsReviewEventSchema
>;

// Map event types to their data types (pre-computed to avoid deep instantiation)
export type SystemEventData = {
  [SystemEvent.EMAIL_INGESTED]: EmailIngestedEvent;
  [SystemEvent.EMAIL_ROUTED_EMAIL_RULE]: EmailRoutedEmailRuleEvent;
  [SystemEvent.EMAIL_ROUTED_AP_INVOICE]: EmailRoutedAPInvoiceEvent;
  [SystemEvent.EMAIL_ROUTED_PURCHASE_ORDER]: EmailRoutedPurchaseOrderEvent;
  [SystemEvent.EMAIL_ROUTED_QUOTE_SHEET]: EmailRoutedQuoteSheetEvent;
  [SystemEvent.AP_INVOICE_DRAFT_CREATED]: APInvoiceDraftCreatedEvent;
  [SystemEvent.AP_INVOICE_PENDING_APPROVAL]: APInvoicePendingApprovalEvent;
  [SystemEvent.AP_INVOICE_NEEDS_REVIEW]: APInvoiceNeedsReviewEvent;
};

export const SystemEventMap = {
  [SystemEvent.EMAIL_INGESTED]: EmailIngestedEventSchema,
  [SystemEvent.EMAIL_ROUTED_EMAIL_RULE]: EmailRoutedEmailRuleEventSchema,
  [SystemEvent.EMAIL_ROUTED_AP_INVOICE]: EmailRoutedAPInvoiceEventSchema,
  [SystemEvent.EMAIL_ROUTED_PURCHASE_ORDER]:
    EmailRoutedPurchaseOrderEventSchema,
  [SystemEvent.EMAIL_ROUTED_QUOTE_SHEET]: EmailRoutedQuoteSheetEventSchema,
  [SystemEvent.AP_INVOICE_DRAFT_CREATED]: APInvoiceDraftCreatedEventSchema,
  [SystemEvent.AP_INVOICE_PENDING_APPROVAL]:
    APInvoicePendingApprovalEventSchema,
  [SystemEvent.AP_INVOICE_NEEDS_REVIEW]: APInvoiceNeedsReviewEventSchema,
} satisfies Record<SystemEvent, z.ZodType>;

export type SystemEventMapType = typeof SystemEventMap;
